import React, { useState, useEffect } from 'react';
import { Gift, Copy, Check, Users, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ReferralCard() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  const fetchReferrals = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/referrals/my`, { credentials: 'include' });
      if (res.ok) setData(await res.json());
    } catch (err) { /* silent */ }
  };

  useEffect(() => { fetchReferrals(); }, []);

  const referralLink = data ? `${window.location.origin}/register?ref=${data.referral_code}` : '';

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      toast.success('Referral link copied!');
      setTimeout(() => setCopied(false), 2500);
    } catch (err) {
      toast.error('Could not copy. Please copy manually.');
    }
  };

  const shareLink = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Errands Boy',
          text: `Sign up to Errands Boy using my code and get R${data.welcome_credit} free credit!`,
          url: referralLink
        });
      } catch (err) { /* user cancelled */ }
    } else {
      copyLink();
    }
  };

  if (!data) return null;

  return (
    <Card data-testid="referral-card" className="bg-gradient-to-br from-slate-900 to-slate-800 border-none rounded-xl p-6 text-white relative overflow-hidden">
      <div className="absolute top-0 right-0 w-40 h-40 bg-yellow-400/10 rounded-full blur-3xl"></div>
      <div className="relative z-10">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Gift className="w-5 h-5 text-yellow-400" strokeWidth={1.5} />
            <h3 className="font-bold text-lg">Invite & Earn</h3>
          </div>
          {data.credit_balance > 0 && (
            <div data-testid="credit-balance-badge" className="bg-yellow-400 text-slate-900 px-3 py-1 rounded-full text-sm font-bold">
              R{data.credit_balance.toFixed(0)} credit
            </div>
          )}
        </div>

        <p className="text-slate-300 text-sm mb-4">
          Share your code — friends get <span className="text-yellow-400 font-medium">R{data.welcome_credit}</span> off,
          you get <span className="text-yellow-400 font-medium">R{data.reward_credit}</span> when they finish their first errand.
        </p>

        <div className="bg-white/10 backdrop-blur rounded-lg p-3 mb-3 border border-white/10">
          <p className="text-xs text-slate-400 mb-1">Your referral code</p>
          <p data-testid="referral-code" className="text-2xl font-bold tracking-widest text-yellow-400">{data.referral_code}</p>
        </div>

        <div className="flex gap-2 mb-4">
          <Button
            data-testid="copy-referral-btn"
            onClick={copyLink}
            className="flex-1 bg-white/10 hover:bg-white/20 text-white rounded-full py-2 text-sm font-medium btn-hover flex items-center justify-center gap-2 border border-white/10"
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? 'Copied!' : 'Copy Link'}
          </Button>
          <Button
            data-testid="share-referral-btn"
            onClick={shareLink}
            className="flex-1 bg-yellow-400 hover:bg-yellow-500 text-slate-900 rounded-full py-2 text-sm font-bold btn-hover flex items-center justify-center gap-2"
          >
            <Sparkles className="w-4 h-4" />
            Share
          </Button>
        </div>

        <div className="flex items-center gap-4 text-xs pt-3 border-t border-white/10">
          <div className="flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5 text-slate-400" />
            <span data-testid="invite-count" className="text-slate-300"><span className="font-bold text-white">{data.total_invites}</span> invited</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Check className="w-3.5 h-3.5 text-yellow-400" />
            <span data-testid="completed-count" className="text-slate-300"><span className="font-bold text-white">{data.completed_referrals}</span> earned</span>
          </div>
        </div>
      </div>
    </Card>
  );
}
